import { z } from 'zod';
import type { ModoArqueo } from '../dominio/caja';
import { centavosPositivos, textoRequerido, uuid } from './comunes';

/** Conteo por denominación: `{ "5000": 2, "2000": 1 }` (centavos → piezas). */
export const conteoSchema = z.record(z.string().regex(/^\d+$/), z.number().int().min(0).max(100_000)).default({});

/**
 * Lo contado en un arqueo: por denominación (`conteo`) o como un solo total por moneda (`total`).
 * Sin `modo` se toma como `conteo`, la forma original.
 */
export const arqueoSchema = z.preprocess(
  (v) => (v && typeof v === 'object' && !('modo' in v) ? { ...v, modo: 'conteo' } : v),
  z.discriminatedUnion('modo', [
    z.object({ modo: z.literal('conteo'), conteoMXN: conteoSchema, conteoUSD: conteoSchema }),
    z.object({ modo: z.literal('total'), totalMXN: centavosPositivos, totalUSD: centavosPositivos.default(0) }),
  ]),
);
export type ArqueoInput = z.infer<typeof arqueoSchema>;

export const aperturaSchema = arqueoSchema;
export type AperturaInput = z.infer<typeof aperturaSchema>;

export const corteSchema = arqueoSchema.and(
  z.object({
    retiroMXN: centavosPositivos.default(0),
    retiroUSD: centavosPositivos.default(0),
  }),
);
export type CorteInput = z.infer<typeof corteSchema>;

export const cierreSchema = arqueoSchema.and(
  z.object({
    fondoSiguienteMXN: centavosPositivos.default(0),
    fondoSiguienteUSD: centavosPositivos.default(0),
  }),
);
export type CierreInput = z.infer<typeof cierreSchema>;

export const reponerSchema = arqueoSchema.and(z.object({ arqueoId: uuid }));
export type ReponerInput = z.infer<typeof reponerSchema>;

export const desbloquearSchema = z.object({
  arqueoId: uuid,
  autorizacionToken: z.string().min(10).optional(),
  /** Sin contraseña a la mano: se envía la solicitud al dueño (app o web). */
  solicitarAlDueno: z.boolean().optional(),
});

/** Retiros e ingresos de la caja. Los gastos salen de la bóveda, no de la caja. */
export const movimientoCajaSchema = z.object({
  tipo: z.enum(['retiro', 'ingreso']),
  moneda: z.enum(['MXN', 'USD']).default('MXN'),
  monto: centavosPositivos.refine((v) => v > 0, 'El monto debe ser mayor a cero'),
  concepto: textoRequerido(200),
});
export type MovimientoCajaInput = z.infer<typeof movimientoCajaSchema>;

export interface ArqueoVista {
  id: string;
  tipo: 'apertura' | 'corte' | 'cierre' | 'reposicion';
  modo: ModoArqueo;
  esperadoMXN: number;
  esperadoUSD: number;
  contadoMXN: number;
  contadoUSD: number;
  diferenciaMXN: number;
  diferenciaUSD: number;
  fecha: string;
  realizadoPor: string;
  desbloqueadoPor: string | null;
}

export interface TurnoCajaVista {
  id: string;
  estado: 'abierto' | 'bloqueado' | 'cerrado';
  responsable: { id: string; nombre: string };
  abiertoEn: string;
  cerradoEn: string | null;
  fondoInicialMXN: number;
  fondoInicialUSD: number;
  esperadoMXN: number;
  esperadoUSD: number;
  ventasPagadas: number;
  totalVendidoMXN: number;
  transferenciasMXN: number;
  /** Cobros con tarjeta del turno (nunca entran al cajón). Comisión nula en MP = 0; neto = bruto - comisión. */
  tarjeta: { brutoMXN: number; comisionMXN: number; netoMXN: number };
  arqueos: ArqueoVista[];
  /** Arqueo con faltante sin resolver (bloquea la caja). */
  arqueoPendiente: ArqueoVista | null;
}
